import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../Layout";
import TenantsTable from "./components/TenantsTable";
import apiService from "../../services/service";
import API_ROUTES_V2 from "../../api-routes-v2";
import "./tenants.scss";
import { Button } from "primereact/button";
import CustomeButton from "~/components/Button";
import useDebounce from "~/hooks/useDebounce";
import FilterDropdown from "~/components/FilterDropdown";
import { getData } from "country-list";
import { getName } from "country-list";
import ReactCountryFlag from "react-country-flag";
import { Chip } from "primereact/chip";

const Tenants = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortMeta, setSortMeta] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedValues, setSelectedValues] = useState({});
  const [chipData, setChipData] = useState([]);
  const [accountManager, setAccountManager] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({
    country: [],
    entity_type: [],
    account_manager: [],
  });
  const debouncedSearchTerm = useDebounce(searchQuery, 500);
  const countries = getData()
    .map((country) => ({
      name: country.name,
      code: country.code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const { country, status, type, account_manager } =
      buildFilterParams(selectedValues);
    fetchTenants({
      pageIndex: page,
      pageSize: rows,
      sortMeta,
      country,
      status,
      type,
      account_manager,
    });
  }, [page, rows, sortMeta]);

  const fetchTenants = async ({
    pageIndex = 0,
    pageSize = 10,
    sortMeta = [],
    skipCache = false,
    status = "",
    country = "",
    type = "",
    account_manager = "",
  } = {}) => {
    try {
      setLoading(true);
      let sortField = sortMeta?.[0]?.field ?? "tenant_name";
      let sortOrder = sortMeta[0]?.order !== 1 ? "asc" : "desc";
      const response = await apiService(API_ROUTES_V2.LIST_TENANTS, "GET", {
        replace: {
          page: pageIndex + 1,
          pageSize,
          sortBy: sortField,
          sortOrder,
          search: searchQuery,
          status,
          type,
          country,
          account_manager,
        },
        skipCache,
      });
      console.log("Tenants response received:", response);

      // Extract tenants from nested response structure
      const tenantsArray = response?.data?.tenants || [];
      const pagination = response?.data?.pagination || {};
      console.log("Extracted tenants array:", tenantsArray);
      console.log("Pagination info:", pagination);

      setTenants(tenantsArray);
      setTotalRecords(pagination.total || 0);
    } catch (error) {
      console.error("Error fetching tenants:", error);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountManager = async () => {
    try {
      const { data } = await apiService(API_ROUTES_V2.FETCH_ACCOUNT_MANAGERS);
      setAccountManager(Array.isArray(data) ? data : []);
    } catch {
      setAccountManager([]);
      console.error("Error fetching account manager types:", error);
    }
  };

  useEffect(() => {
    fetchAccountManager();
  }, []);
  const handleCreateTenant = () => {
    navigate("/tenants/onboard");
  };

  const onPageChange = (event) => {
    setPage(event.page);
    setRows(event.rows);
  };

  const onSort = (event) => {
    setSortMeta(event.multiSortMeta || []);
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleChipData = (selectedValues) => {
    let chipArray = [];
    for (const key in selectedValues) {
      selectedValues[key].map((data) => {
        chipArray.push({ category: key, value: data });
      });
    }
    setChipData(chipArray);
  };
  const buildFilterParams = (filters) => {
    return {
      country: filters.country?.join(",") || "",
      status: filters.status?.join(",") || "",
      type: filters.type?.join(",") || "",
      account_manager: filters.account_manager?.join(",") || "",
    };
  };

  const applyFilters = () => {
    setSelectedValues(selectedValues);
    handleChipData(selectedValues);
    setPage(0);
    setRows(10);
    setAppliedFilter(selectedFilters);
    const { country, status, type, account_manager } =
      buildFilterParams(selectedValues);
    fetchTenants({
      pageIndex: 0,
      pageSize: 10,
      sortMeta,
      country,
      status,
      type,
      account_manager,
    });
  };
  const deSelectAll = () => {
    setSelectedFilters({
      country: [],
      entity_type: [],
      account_manager: [],
    });
    setAppliedFilter({});
    setSelectedValues({});
    setChipData([]);
    fetchTenants({
      pageIndex: 0,
      pageSize: 10,
      sortMeta,
      country: "",
      status: "",
      type: "",
    });
  };

  const handleRemoveFilter = (data) => {
    const array =
      selectedValues[data.category]?.filter((value) => {
        return value !== data?.value;
      }) || [];

    delete selectedFilters[data?.value];
    setSelectedFilters(selectedFilters);
    const filteredValue = {
      ...selectedValues,
      [data.category]: array,
    };
    setPage(0);
    setRows(10);
    handleChipData(filteredValue);
    setSelectedValues(filteredValue);
    const { country, status, type, account_manager } =
      buildFilterParams(filteredValue);
    fetchTenants({
      pageIndex: 0,
      pageSize: 10,
      sortMeta,
      country,
      status,
      type,
      account_manager,
    });
  };
  useEffect(() => {
    const { country, status, type, account_manager } =
      buildFilterParams(selectedValues);

    fetchTenants({
      pageIndex: 0,
      pageSize: 10,
      sortMeta,
      country,
      status,
      type,
      account_manager,
    });
  }, [debouncedSearchTerm]);
  const getManagerName = (data) => {
    if (data?.preferred_name) {
      return data?.preferred_name;
    } else {
      const givenName = data?.given_name || "";
      const surname = data?.surname || "";
      return `${givenName} ${surname}`.trim() || "-";
    }
  };
  const filterOptions = useMemo(() => {
    return [
      {
        name: "Country",
        id: "country",
        suboptions: countries.map((country) => {
          return {
            name: getName(country.code),
            code: country.code,
            icon: () => (
              <ReactCountryFlag
                className="country__flag"
                countryCode={country.code}
                svg
              />
            ),
          };
        }),
      },
      {
        name: "Type",
        id: "type",
        suboptions: [
          { name: "Customer", code: "CUSTOMER" },
          { name: "Local Team", code: "LSP" },
        ],
      },
      {
        name: "Status",
        id: "status",
        suboptions: [
          { name: "Active", code: "ACTIVE" },
          { name: "Inactive", code: "INACTIVE" },
        ],
      },
      {
        name: "Account manager",
        id: "account_manager",
        suboptions: (accountManager || [])?.map((data) => {
          return {
            name: getManagerName(data),
            code: data?.id,
          };
        }),
      },
    ];
  }, [accountManager]);
  const getChipValue = (data) => {
    if (data?.category === "status") {
      return data?.value === "ACTIVE" ? "Active" : "Inactive";
    } else if (data?.category === "type") {
      return data?.value === "CUSTOMER" ? "Customer" : "Local Team";
    } else if (data?.category === "account_manager") {
      const manager = accountManager?.find((value) => {
        return value?.id === data?.value;
      });
      return getManagerName(manager);
    }
  };
  const showTable =
    !loading &&
    !tenants.length &&
    !chipData.length &&
    !debouncedSearchTerm?.trim();
  return (
    <Layout
      title="Tenants"
      subtitle="Locate all parent companies we manage in our system. Easily search, filter, and access key information for effective management."
    >
      <div className="tenants__page">
        <div className="flex align-items-center">
          <div className="flex-1 mb-0 p-0" style={{ paddingBottom: "5px" }}>
            <div className="input-search-container">
              <span className="pi pi-search"></span>
              <input
                type="text"
                placeholder="Search tenant by name or country"
                className="appearance-none outline-none w-full"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="pi pi-search"></span>
            </div>
          </div>
          <FilterDropdown
            groupedOptions={filterOptions}
            filterCount={chipData?.length}
            value={selectedFilters}
            onChange={(e) => {
              setSelectedValues(e.grouped);
              setSelectedFilters(e.value);
            }}
            applyFilter={applyFilters}
            deSelectAll={deSelectAll}
            placeholder="All events"
            onHide={() => {
              setSelectedFilters(appliedFilter);
            }}
          />
          <div className="tenants__table-header">
            <CustomeButton
              className="btn-add-tenant"
              type="button"
              onClick={handleCreateTenant}
            >
              Add New Tenant
              <i className="pi pi-plus ml-2" />
            </CustomeButton>
          </div>
        </div>
        <div className="filter-chips-container">
          {chipData?.length ? `Filters (${chipData?.length})` : null}
          {chipData?.map((data) => (
            <Chip
              key={`${data?.category}-${data?.value}`}
              label={
                <span
                  key={`${data?.category}-${data?.value}`}
                  className="filter__chip"
                >
                  {data?.category === "country" ? (
                    <span>
                      <ReactCountryFlag countryCode={data?.value} svg />
                      {getName(data?.value)}
                    </span>
                  ) : (
                    getChipValue(data)
                  )}{" "}
                  <span className="cross__icon">
                    <i
                      className="pi pi-times"
                      onClick={() => handleRemoveFilter(data)}
                    />
                  </span>
                </span>
              }
              removable={false}
            />
          ))}
        </div>
        <div className="card">
          {showTable ? (
            <div className="tenants__empty-state">
              <div className="tenants__empty-icon">
                <svg
                  width="136"
                  height="136"
                  viewBox="0 0 136 136"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_d_19_4555)">
                    <rect
                      x="12"
                      y="10"
                      width="112"
                      height="112"
                      rx="40"
                      fill="white"
                      shapeRendering="crispEdges"
                    />
                    <path
                      d="M50 34H86C89.25 34 92 36.75 92 40V92C92 95.375 89.25 98 86 98H74V88C74 84.75 71.25 82 68 82C64.625 82 62 84.75 62 88V98H50C46.625 98 44 95.375 44 92V40C44 36.75 46.625 34 50 34ZM52 64V68C52 69.125 52.875 70 54 70H58C59 70 60 69.125 60 68V64C60 63 59 62 58 62H54C52.875 62 52 63 52 64ZM66 62C64.875 62 64 63 64 64V68C64 69.125 64.875 70 66 70H70C71 70 72 69.125 72 68V64C72 63 71 62 70 62H66ZM76 64V68C76 69.125 76.875 70 78 70H82C83 70 84 69.125 84 68V64C84 63 83 62 82 62H78C76.875 62 76 63 76 64ZM54 46C52.875 46 52 47 52 48V52C52 53.125 52.875 54 54 54H58C59 54 60 53.125 60 52V48C60 47 59 46 58 46H54ZM64 48V52C64 53.125 64.875 54 66 54H70C71 54 72 53.125 72 52V48C72 47 71 46 70 46H66C64.875 46 64 47 64 48ZM78 46C76.875 46 76 47 76 48V52C76 53.125 76.875 54 78 54H82C83 54 84 53.125 84 52V48C84 47 83 46 82 46H78Z"
                      fill="#188A42"
                    />
                    <path
                      opacity="0.4"
                      d="M50 34H86C89.25 34 92 36.75 92 40V92C92 95.375 89.25 98 86 98H74V88C74 84.75 71.25 82 68 82C64.625 82 62 84.75 62 88V98H50C46.625 98 44 95.375 44 92V40C44 36.75 46.625 34 50 34ZM52 64V68C52 69.125 52.875 70 54 70H58C59 70 60 69.125 60 68V64C60 63 59 62 58 62H54C52.875 62 52 63 52 64ZM66 62C64.875 62 64 63 64 64V68C64 69.125 64.875 70 66 70H70C71 70 72 69.125 72 68V64C72 63 71 62 70 62H66ZM76 64V68C76 69.125 76.875 70 78 70H82C83 70 84 69.125 84 68V64C84 63 83 62 82 62H78C76.875 62 76 63 76 64ZM54 46C52.875 46 52 47 52 48V52C52 53.125 52.875 54 54 54H58C59 54 60 53.125 60 52V48C60 47 59 46 58 46H54ZM64 48V52C64 53.125 64.875 54 66 54H70C71 54 72 53.125 72 52V48C72 47 71 46 70 46H66C64.875 46 64 47 64 48ZM78 46C76.875 46 76 47 76 48V52C76 53.125 76.875 54 78 54H82C83 54 84 53.125 84 52V48C84 47 83 46 82 46H78Z"
                      fill="#188A42"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_d_19_4555"
                      x="0"
                      y="0"
                      width="136"
                      height="136"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="2" />
                      <feGaussianBlur stdDeviation="6" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_19_4555"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_19_4555"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>
              <h3 className="tenants__empty-heading">
                Looks like there aren't any tenants signed up yet.
              </h3>
              <p className="tenants__empty-description">
                Let's add your first one
              </p>
              <button
                className="tenants__empty-button"
                type="button"
                onClick={handleCreateTenant}
              >
                Add New Tenant
                <i className="pi pi-plus ml-2" />
              </button>
            </div>
          ) : (
            <TenantsTable
              data={tenants}
              loading={loading}
              page={page}
              rows={rows}
              totalRecords={totalRecords}
              onPageChange={onPageChange}
              sortMeta={sortMeta}
              onSort={onSort}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Tenants;
